import { FC, useEffect, useState } from "react";
import { useTrans } from "../../../hooks";
import { useAuthData } from "../../../contexts";
import {
    Button,
    HStack,
    Input,
    InputGroup,
    InputLeftElement,
    Radio,
    RadioGroup,
    SimpleGrid,
    VStack,
} from "@chakra-ui/react";
import {
    Company,
    CompanyApi,
    User as Entity,
    UserApi as EntityApi,
    School,
    SchoolApi,
    SchoolBatch,
    SchoolBatchApi,
    ServiceApi,
    ServiceGroup,
    ServiceGroupApi,
} from "../../../apis";
import { Controller, useForm } from "react-hook-form";
import { AppFilterContainer } from "../../../containers";
import {
    AppFormControl,
    AppIcon,
    AppMultiSelector,
    AppMultiSelectorOption,
    AppMultiSelectorOptionType,
    AppSelect,
    AppSelectOption,
} from "../../../components";
import { useRecoilValue } from "recoil";
import { ActiveSchoolOrCompanyAtom } from "../../../atoms";
import { APPC, UTIL } from "../../../config";

export type UserFilterDataType = {
    user_search: string;
    userType: string;
    studentTrainer: string;
    companyServices: string[];
    nationality: string;
    schoolBatch: string;
    status: string;
    isActive: boolean;
};

const defaultValues: UserFilterDataType = {
    user_search: "",
    userType: "",
    studentTrainer: "",
    companyServices: [],
    nationality: "",
    schoolBatch: "",
    status: "",
    isActive: false,
};

export const UserFilter: FC<{
    filterData: any;
    setFilterData: (data: any) => void;
    children: JSX.Element;
}> = ({ filterData, setFilterData, children }) => {
    // hook
    const { t } = useTrans();
    const { user, grant } = useAuthData();
    const activeSchoolOrCompany = useRecoilValue(ActiveSchoolOrCompanyAtom);

    // state & const
    const USERTYPE = APPC.BE.User.USERTYPE;
    const [isInit, setIsInit] = useState(false);
    const [selectedSchools, setSelectedSchools] = useState<
        AppMultiSelectorOption[]
    >([]);
    const [selectedCompanies, setSelectedCompanies] = useState<
        AppMultiSelectorOption[]
    >([]);
    const [selectedUserType, setSelectedUserType] =
        useState<AppSelectOption | null>(
            filterData?.userType
                ? {
                      value: filterData?.userType,
                      label: t(`con.User:USERTYPE_${filterData?.userType}`),
                  }
                : null
        );
    const [loadingStudentTrainers, setLoadingStudentTrainers] = useState(false);
    const [studentTrainerOptions, setStudentTrainerOptions] = useState<
        AppSelectOption[]
    >([]);
    const [selectedStudentTrainer, setSelectedStudentTrainer] =
        useState<AppSelectOption | null>(null);
    const [loadingService, setLoadingService] = useState(false);
    const [serviceGroupedOptions, setServiceGroupedOptions] = useState<
        { label: string; options: AppSelectOption[] }[]
    >([]);
    const [selectedServices, setSelectedServices] = useState<AppSelectOption[]>(
        []
    );
    const [selectedNationality, setSelectedNationality] =
        useState<AppSelectOption | null>(
            filterData?.nationality
                ? {
                      value: filterData?.nationality,
                      label: filterData?.nationality,
                  }
                : null
        );
    const [selectedStatus, setSelectedStatus] =
        useState<AppSelectOption | null>(
            filterData?.status
                ? {
                      value: filterData?.status,
                      label: filterData?.status,
                  }
                : null
        );
    const [loadingSchoolBatch, setLoadingSchoolBatch] = useState(false);
    const [schoolBatchOptions, setSchoolBatchOptions] = useState<
        AppSelectOption[]
    >([]);
    const [selectedSchoolBatch, setSelectedSchoolBatch] =
        useState<AppSelectOption | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const { handleSubmit, register, reset, setValue, control } =
        useForm<UserFilterDataType>({
            defaultValues: filterData,
            mode: "onSubmit",
        });

    // handler
    const onSubmitHandler = (formData: UserFilterDataType) => {
        const filterParams = {};
        Object.keys(formData).forEach((key) => {
            if (formData[key] || formData[key] === false) {
                let nKey = key;
                switch (key) {
                    case "studentTrainer":
                    case "companyServices":
                    case "schoolBatch":
                        nKey = `${key}.id`;
                        break;
                }
                if (
                    (key === "companyServices" &&
                        formData["companyServices"].length > 0) ||
                    key !== "companyServices"
                ) {
                    filterParams[nKey] = formData[key];
                }
            }
        });
        if (selectedSchools.length > 0 && selectedSchools[0].value) {
            filterParams["schools.id"] = selectedSchools[0].value;
        }
        if (selectedCompanies.length > 0 && selectedCompanies[0].value) {
            filterParams["companies.id"] = selectedCompanies[0].value;
        }
        setFilterData(filterParams);
    };

    const loadSchoolOptions = async (
        page: number,
        search: string,
        pageSize: number
    ): Promise<AppMultiSelectorOptionType> => {
        const params = {
            itemsPerPage: pageSize,
            [`order[name]`]: "asc",
            pagination: true,
        };

        if (search) {
            params["name"] = search;
        }

        const { response } = await SchoolApi.getCollection<School>(
            page,
            params
        );

        if (response !== null) {
            const newOptions =
                response.items?.map((e) => ({
                    value: SchoolApi.toResourceIRI(e.id),
                    label: `${e.id} ${e.name}`,
                })) || [];
            return {
                data: newOptions,
                totalItems: response.totalItems,
            };
        }

        return { data: [], totalItems: 0 };
    };

    const loadCompanyOptions = async (
        page: number,
        search: string,
        pageSize: number
    ): Promise<AppMultiSelectorOptionType> => {
        const params = {
            itemsPerPage: pageSize,
            [`order[name]`]: "asc",
            pagination: true,
        };

        if (search) {
            params["name"] = search;
        }

        const { response } = await CompanyApi.getCollection<Company>(
            page,
            params
        );

        if (response !== null) {
            const newOptions =
                response.items?.map((e) => ({
                    value: CompanyApi.toResourceIRI(e.id),
                    label: `${e.id} ${e.name}`,
                })) || [];
            return {
                data: newOptions,
                totalItems: response.totalItems,
            };
        }

        return { data: [], totalItems: 0 };
    };

    const loadStudentTrainerOptions = () => {
        if (
            activeSchoolOrCompany?.["@type"] === "School" ||
            (selectedSchools && selectedSchools.length > 0)
        ) {
            const params = {
                [`order[name]`]: "asc",
                pagination: false,
                ["schools.id"]:
                    activeSchoolOrCompany?.id || selectedSchools[0]?.value,
                userType: USERTYPE.USERTYPE_PEDAGOGICAL_REFERENCE_TRAINER,
            };
            setLoadingStudentTrainers(true);
            EntityApi.getCollection<Entity>(1, params)
                .then(({ response }) => {
                    if (response !== null) {
                        setStudentTrainerOptions(
                            response?.items?.map((e) => ({
                                value: EntityApi.toResourceIRI(e.id),
                                label: e.fullName,
                            })) || []
                        );
                    }
                })
                .finally(() => setLoadingStudentTrainers(false));
        }
    };

    const loadSchoolBatchOptions = () => {
        if (
            activeSchoolOrCompany?.["@type"] === "School" ||
            (selectedSchools && selectedSchools.length > 0)
        ) {
            const params = {
                [`order[name]`]: "asc",
                pagination: false,
                ["school.id"]:
                    activeSchoolOrCompany?.id || selectedSchools[0]?.value,
            };
            setLoadingSchoolBatch(true);
            SchoolBatchApi.getCollection<SchoolBatch>(1, params)
                .then(({ response }) => {
                    if (response !== null) {
                        setSchoolBatchOptions(
                            response?.items?.map((e) => ({
                                value: SchoolBatchApi.toResourceIRI(e.id),
                                label: e.name,
                            })) || []
                        );
                    }
                })
                .finally(() => setLoadingSchoolBatch(false));
        }
    };

    const loadServiceOptions = () => {
        if (
            activeSchoolOrCompany?.["@type"] === "Company" ||
            (selectedCompanies && selectedCompanies.length > 0)
        ) {
            const params = {
                [`order[name]`]: "asc",
                pagination: false,
                ["company.id"]:
                    activeSchoolOrCompany?.id || selectedCompanies[0]?.value,
            };
            setLoadingService(true);
            ServiceGroupApi.getCollection<ServiceGroup>(1, params)
                .then(({ response }) => {
                    if (response !== null) {
                        const options = response.items.map((group) => ({
                            label: group.name,
                            options: (group.services || []).map((service) => ({
                                value: ServiceApi.toResourceIRI(service.id),
                                label: service.name,
                            })),
                        }));
                        setServiceGroupedOptions(options);
                    }
                })
                .finally(() => setLoadingService(false));
        }
    };

    const getUserTypeOptions = (userType: string): AppSelectOption[] => {
        if (grant.isSuperAdmin) {
            return Object.values(USERTYPE).map((ut) => ({
                value: ut,
                label: t(`con.User:USERTYPE_${ut}`),
            }));
        } else if (activeSchoolOrCompany) {
            return UTIL.userFilterTypeAllow(
                userType,
                activeSchoolOrCompany?.["@type"] === "School"
            ).map((ut) => ({
                value: ut,
                label: t(`con.User:USERTYPE_${ut}`),
            }));
        }
        return [];
    };

    const resetForm = () => {
        setFilterData({});
        setSelectedSchools([]);
        setSelectedCompanies([]);
        setSelectedUserType(null);
        setSelectedStudentTrainer(null);
        setSelectedSchoolBatch(null);
        setSelectedNationality(null);
        setSelectedStatus(null);
        setSelectedServices([]);
        reset(defaultValues);
    };

    // useEffect
    useEffect(() => {
        if (isInit) {
            setStudentTrainerOptions([]);
            setSelectedStudentTrainer(null);
            setSchoolBatchOptions([]);
            setSelectedSchoolBatch(null);
            loadStudentTrainerOptions();
            loadSchoolBatchOptions();
        }
    }, [isInit, selectedSchools, activeSchoolOrCompany]);

    useEffect(() => {
        if (isInit) {
            setSelectedServices([]);
            loadServiceOptions();
        }
    }, [isInit, selectedCompanies, activeSchoolOrCompany]);

    return (
        <AppFilterContainer
            onReset={resetForm}
            triggerElement={children}
            setIsInit={setIsInit}
            isModalOpen={isModalOpen}
            zIndex={"1400"}
        >
            <form
                id="app-filter-form"
                onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit(onSubmitHandler)();
                }}
            >
                <VStack gap={4} alignItems={"stretch"} justify={"start"}>
                    <AppFormControl label={t("cmn:label.search")}>
                        <Controller
                            control={control}
                            name={"user_search"}
                            defaultValue={filterData?.user_search || ""}
                            render={({ field }) => (
                                <InputGroup>
                                    <InputLeftElement pointerEvents="none">
                                        <AppIcon name="ics-search" w="1rem" />
                                    </InputLeftElement>
                                    <Input {...field} />
                                </InputGroup>
                            )}
                        />
                    </AppFormControl>
                    <SimpleGrid
                        columns={{ sm: 1, md: 2 }}
                        spacingX={5}
                        spacingY={4}
                        w={"full"}
                    >
                        {grant.isSuperAdmin && (
                            <>
                                <AppFormControl
                                    label={t("ent.User:school.label")}
                                >
                                    <AppMultiSelector
                                        onSelect={(data) => {
                                            setSelectedSchools(data);
                                            setIsModalOpen(false);
                                        }}
                                        loadOptions={loadSchoolOptions}
                                        selectedOpt={selectedSchools}
                                        subject={t("ent.User:school.label")}
                                        triggerElement={
                                            <Button
                                                size={"sm"}
                                                variant={"secondary"}
                                                onClick={() =>
                                                    setIsModalOpen(true)
                                                }
                                            >
                                                {t(
                                                    "padm.UserForm:button.selectSchool"
                                                )}
                                            </Button>
                                        }
                                        isModal={true}
                                        isSearch={true}
                                        selectorRows={8}
                                        maxSelectOptions={
                                            UTIL.isSchoolCompanyMultiSelect(
                                                user?.userType
                                            )
                                                ? undefined
                                                : 1
                                        }
                                        setIsModalOpen={setIsModalOpen}
                                    />
                                </AppFormControl>
                                <AppFormControl
                                    label={t("ent.User:company.label")}
                                >
                                    <AppMultiSelector
                                        onSelect={(data) => {
                                            setSelectedCompanies(data);
                                            setServiceGroupedOptions([]);
                                            setValue("companyServices", []);
                                            setIsModalOpen(false);
                                        }}
                                        loadOptions={loadCompanyOptions}
                                        selectedOpt={selectedCompanies}
                                        subject={t("ent.User:company.label")}
                                        triggerElement={
                                            <Button
                                                size={"sm"}
                                                variant={"secondary"}
                                                onClick={() =>
                                                    setIsModalOpen(true)
                                                }
                                            >
                                                {t(
                                                    "padm.UserForm:button.selectCompany"
                                                )}
                                            </Button>
                                        }
                                        isModal={true}
                                        isSearch={true}
                                        selectorRows={8}
                                        maxSelectOptions={
                                            UTIL.isSchoolCompanyMultiSelect(
                                                user?.userType
                                            )
                                                ? undefined
                                                : 1
                                        }
                                        setIsModalOpen={setIsModalOpen}
                                    />
                                </AppFormControl>
                            </>
                        )}
                        <AppFormControl label={t("ent.User:userType.label")}>
                            <Controller
                                control={control}
                                name="userType"
                                render={({ field }) => (
                                    <AppSelect<AppSelectOption>
                                        name={field.name}
                                        isClearable
                                        options={getUserTypeOptions(
                                            user?.userType || ""
                                        )}
                                        value={selectedUserType}
                                        placeholder={t(
                                            "ent.User:userType.placeholder"
                                        )}
                                        onChange={(e) => {
                                            field.onChange(e?.value || null);
                                            setSelectedUserType(
                                                e as AppSelectOption
                                            );
                                            setSelectedStudentTrainer(null);
                                            setSelectedNationality(null);
                                            setSelectedStatus(null);
                                            setSelectedServices([]);
                                            setSelectedSchoolBatch(null);
                                            setValue("studentTrainer", "");
                                            setValue("nationality", "");
                                            setValue("status", "");
                                            setValue("companyServices", []);
                                            setValue("schoolBatch", "");
                                        }}
                                    />
                                )}
                            />
                        </AppFormControl>
                        {selectedUserType?.value ===
                            USERTYPE.USERTYPE_STUDENT && (
                            <>
                                {((selectedSchools.length > 0 &&
                                    selectedSchools[0].value) ||
                                    activeSchoolOrCompany) && (
                                    <AppFormControl
                                        label={t(
                                            "ent.User:studentTrainer.label"
                                        )}
                                    >
                                        <Controller
                                            control={control}
                                            name="studentTrainer"
                                            render={({ field }) => (
                                                <AppSelect<AppSelectOption>
                                                    name={field.name}
                                                    isClearable
                                                    isLoading={
                                                        loadingStudentTrainers
                                                    }
                                                    options={
                                                        studentTrainerOptions
                                                    }
                                                    value={
                                                        selectedStudentTrainer ||
                                                        null
                                                    }
                                                    placeholder={t(
                                                        "ent.User:studentTrainer.placeholder"
                                                    )}
                                                    onChange={(e) => {
                                                        field.onChange(
                                                            e?.value || null
                                                        );
                                                        setSelectedStudentTrainer(
                                                            e as AppSelectOption
                                                        );
                                                    }}
                                                />
                                            )}
                                        />
                                    </AppFormControl>
                                )}
                                <AppFormControl
                                    label={t("ent.User:nationality.label")}
                                >
                                    <Controller
                                        control={control}
                                        name="nationality"
                                        render={({ field }) => (
                                            <AppSelect<AppSelectOption>
                                                name={field.name}
                                                isClearable
                                                options={Object.values(
                                                    APPC.BE.User.NATIONALITY
                                                ).map((nat) => ({
                                                    value: nat,
                                                    label: nat,
                                                }))}
                                                value={selectedNationality}
                                                placeholder={t(
                                                    "ent.User:nationality.placeholder"
                                                )}
                                                onChange={(e) => {
                                                    field.onChange(
                                                        e?.value || null
                                                    );
                                                    setSelectedNationality(
                                                        e as AppSelectOption
                                                    );
                                                }}
                                            />
                                        )}
                                    />
                                </AppFormControl>
                                <AppFormControl
                                    label={t("ent.User:status.label")}
                                >
                                    <Controller
                                        control={control}
                                        name="status"
                                        render={({ field }) => (
                                            <AppSelect<AppSelectOption>
                                                name={field.name}
                                                isClearable
                                                options={Object.values(
                                                    APPC.BE.User.STUDENT_STATUS
                                                ).map((s) => ({
                                                    value: s,
                                                    label: t(
                                                        `con.User:STUDENT_STATUS_${s}`
                                                    ),
                                                }))}
                                                value={selectedStatus}
                                                placeholder={t(
                                                    "ent.User:status.placeholder"
                                                )}
                                                onChange={(e) => {
                                                    field.onChange(
                                                        e?.value || null
                                                    );
                                                    setSelectedStatus(
                                                        e as AppSelectOption
                                                    );
                                                }}
                                            />
                                        )}
                                    />
                                </AppFormControl>
                                <AppFormControl
                                    label={t("ent.User:schoolBatch.label")}
                                >
                                    <Controller
                                        control={control}
                                        name="schoolBatch"
                                        render={({ field }) => (
                                            <AppSelect<AppSelectOption>
                                                name={field.name}
                                                isClearable
                                                isLoading={loadingSchoolBatch}
                                                options={schoolBatchOptions}
                                                value={
                                                    selectedSchoolBatch || null
                                                }
                                                placeholder={t(
                                                    "ent.User:schoolBatch.placeholder"
                                                )}
                                                onChange={(e) => {
                                                    field.onChange(
                                                        e?.value || null
                                                    );
                                                    setSelectedSchoolBatch(
                                                        e as AppSelectOption
                                                    );
                                                }}
                                            />
                                        )}
                                    />
                                </AppFormControl>
                            </>
                        )}
                        {selectedUserType?.value ===
                            USERTYPE.USERTYPE_INTERNSHIP_SUPERVISOR && (
                            <>
                                {((selectedCompanies.length > 0 &&
                                    selectedCompanies[0].value) ||
                                    activeSchoolOrCompany) && (
                                    <AppFormControl
                                        label={t(
                                            "ent.User:companyServices.label"
                                        )}
                                    >
                                        <Controller
                                            control={control}
                                            name="companyServices"
                                            render={({ field }) => (
                                                <AppSelect<
                                                    AppSelectOption,
                                                    true
                                                >
                                                    name={field.name}
                                                    isClearable
                                                    isMulti={true}
                                                    isLoading={loadingService}
                                                    options={
                                                        serviceGroupedOptions
                                                    }
                                                    value={
                                                        selectedServices || null
                                                    }
                                                    placeholder={t(
                                                        "ent.User:companyServices.placeholder"
                                                    )}
                                                    onChange={(e) => {
                                                        field.onChange(
                                                            e?.map(
                                                                (option) =>
                                                                    option?.value
                                                            ) || []
                                                        );
                                                        setSelectedServices(
                                                            e as AppSelectOption[]
                                                        );
                                                    }}
                                                />
                                            )}
                                        />
                                    </AppFormControl>
                                )}
                            </>
                        )}
                    </SimpleGrid>
                    <AppFormControl label={t("ent.User:isActive.label")}>
                        <RadioGroup
                            defaultValue={filterData?.isActive}
                            w={"full"}
                        >
                            <HStack alignItems={"start"} w={"full"}>
                                <Radio {...register("isActive")} value={""}>
                                    {t("cmn:label.none")}
                                </Radio>
                                <Radio {...register("isActive")} value={"true"}>
                                    {t("cmn:label.yes")}
                                </Radio>
                                <Radio
                                    {...register("isActive")}
                                    value={"false"}
                                >
                                    {t("cmn:label.no")}
                                </Radio>
                            </HStack>
                        </RadioGroup>
                    </AppFormControl>
                </VStack>
            </form>
        </AppFilterContainer>
    );
};
